import {
  Condition,
  ConditionGroup, TranslatedQuery,
} from '@ajax/vue/components/templates/item-list/condition/contactlens/model/condition'
import { none, Option } from 'fp-ts/lib/Option'

/**
 * 絞り込み画面がユーザーに提供するUI操作とそれに伴う状態管理
 *
 * ここで言う「UI操作」はあくまでこのUI実装内部でユーザーが行う操作に限定する.
 * URLそのものの変更やブラウザ操作など、UI実装外での「UI操作」およびその結果までは取り扱わない.
 * つまり、ここで言う「UI操作」は「ユーザーが取りうるあらゆるUI操作」を意味しない.
 */
export interface Interaction {
  readonly sections: Section[]
  readonly conditionAsQuery: TranslatedQuery

  toggle(name: SectionName, target: Condition): Interaction
  reset(): Interaction

  findCondition(name: string, f: (cg: ConditionGroup) => Option<Condition>): Option<Condition>
}

export const notFilledInteraction: Interaction = {
  sections: [],
  conditionAsQuery: TranslatedQuery.empty,
  toggle: (_name, _target) => notFilledInteraction,
  reset: () => notFilledInteraction,
  findCondition: () => none,
}
/**
 * @note
 * {@see InteractionState}の具体的なキーには特に依存していないので、
 * ジェネリクスを活用すれば既存の
 * client/ajax/src/vue/components/templates/item-list/condition/default/Condition.vue
 * とも統合できるかも
 */
export class FulfilledInteraction implements Interaction {
  constructor(
    private readonly state: InteractionState
  ) {}

  get sections(): Section[] {
    return this.keys.map(name => ({
      name,
      conditionGroup: this.state[name],
    }))
  }

  toggle(name: SectionName, target: Condition): Interaction {
    return new FulfilledInteraction({
      ...this.state,
      [name]: this.state[name].change({ target, applied: !target.applied }),
    })
  }
  reset(): FulfilledInteraction {
    return new FulfilledInteraction(
      this.keys.reduce(
        (acc, name) => ({ ...acc, [name]: this.state[name].reset() }),
        this.state
      )
    )
  }

  findCondition(name: SectionName, f: (cg: ConditionGroup) => Option<Condition>): Option<Condition> {
    return f(this.state[name])
  }

  get conditionAsQuery(): TranslatedQuery {
    return this.sections.map(section => section.conditionGroup.translateToQuery()).reduce(
      (acc, q) => acc.concat(q),
      TranslatedQuery.empty
    )
  }

  private get keys(): ReadonlyArray<keyof InteractionState> {
    return Object.keys(this.state) as ReadonlyArray<keyof InteractionState>
  }
}
export interface InteractionState {
  /** 装用期間 */
  readonly disposalSchedule: ConditionGroup
  /** DIA(diameter) */
  readonly dia: ConditionGroup
  /** カラー */
  readonly color: ConditionGroup
  /**
   * 度あり・なし
   *
   * 度ありのメガネ = prescription range
   */
  readonly prescriptionOrNot: ConditionGroup
  /** BC(base curve) */
  readonly bc: ConditionGroup
  /** 着色直径 */
  readonly colorDia: ConditionGroup
  /** フチあり・なし */
  readonly withBorderOrNot: ConditionGroup
  /** 高度数 */
  // https://github.com/Everforth/growthpalette-front/issues/7906 では対応しない
  // https://github.com/Everforth/growthpalette-pm/issues/239#issuecomment-1959156777
  /** 含水率 */
  readonly liquidRetainedRatio: ConditionGroup
  /** オプション */
  readonly option: ConditionGroup
}

export type SectionName = keyof InteractionState
export interface Section {
  readonly name: SectionName
  readonly conditionGroup: ConditionGroup
}
